/**
 * Which page template each entry of `GET_BRIEF_SHIP_DESTINATION_LISTS_V2` renders, and
 * the slug its store-visit URL carries.
 *
 * ## uids are per category, not global
 *
 * The single most important fact about that response: **its `uid` is only unique within a
 * category.** `uid: 27` is *both* "Harmony of the Seas" (ship) and "Perfect Day CocoCay,
 * Bahamas" (destination). Any lookup that searches ships and destinations together will
 * therefore hand some ships a destination's template — which is exactly what happened when
 * V2 renumbered the fleet: Legend and Hero landed on uids 7 and 8, the two the old
 * destination list claimed, and opened the CocoCay and Santorini pages.
 *
 * Every lookup here is category-scoped for that reason. `findDestinationEntry` refuses a
 * non-destination outright rather than trusting the caller to have filtered first.
 */

/**
 * The destinations that have a page of their own, keyed by their V2 `uid`.
 *
 * Only these two. Everything else — including Royal Beach Club Paradise Island (uid 17) —
 * resolves to `destination-generic`, so this list stays short and does not have to keep
 * pace with whatever BE adds.
 *
 * ⚠️ These uids come from the response, and V2 already renumbered them once (CocoCay 7→27,
 * Santorini 8→16). If CocoCay or Santorini ever opens the generic page, check here first.
 */
export const DESTINATIONS = [
    {
        title: "Perfect Day CocoCay, Bahamas",
        category: "destination",
        brief_ship_destination_uid: 27,
        type: "destination",
        slug: "cococay",
    },
    {
        title: "Royal Beach Club Santorini",
        category: "destination",
        brief_ship_destination_uid: 16,
        type: "destination-santorini",
        slug: "santorini",
    },
];

/**
 * The ships that get the enhanced page. Everything else in the fleet gets the standard one.
 *
 * Held by name rather than by uid, and that is not a convenience — it is the only thing that
 * survives BE renumbering. V2 moved Legend from uid 3 to 7, Star from 4 to 5, Utopia from 5
 * to 6 and Icon from 6 to 4; a uid-keyed table would have silently handed four ships each
 * other's templates. Names did not move.
 *
 * Keyed on the distinguishing word so the match survives BE's casing and spacing: "Hero of
 * the Seas", "HERO OF THE SEAS" and "Hero Of The Seas" all reduce to "hero".
 */
const ENHANCED_SHIP_KEYS = new Set([
    "legend",
    "star",
    "icon",
    "utopia",
    "hero",
    "wonder",
    "allure",
    "harmony",
    "oasis",
    "symphony",
]);

const shipKey = (title) =>
    String(title ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+of\s+the\s+seas$/, "")
        .trim();

const isCategory = (category, wanted) =>
    String(category ?? "").trim().toLowerCase() === wanted;

/** "Perfect Day CocoCay, Bahamas" -> "perfect-day-cococay-bahamas". */
const slugify = (title) =>
    String(title ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

/**
 * The `DESTINATIONS` entry for a brief item, or `null`.
 *
 * Category-scoped on purpose — see the header of this file. A ship is refused outright, so
 * a ship sharing a uid with CocoCay or Santorini cannot pick up their template.
 */
export const findDestinationEntry = (uid, category) => {
    if (!isCategory(category, "destination")) return null;
    return DESTINATIONS.find((entry) => entry.brief_ship_destination_uid === uid) ?? null;
};

/**
 * Which page template an entry of the brief list renders.
 *
 * **Ships are resolved by name, never by uid.** `ENHANCED_SHIP_KEYS` names the ten on the
 * enhanced page and the rest of the fleet takes the standard one, so every ship BE returns
 * gets a template whether or not anything here has heard of it. A name BE spells differently
 * than expected falls to the standard template rather than to `null` — the safe direction to
 * be wrong in, since a page that renders beats a dead end.
 *
 * **Destinations** take a uid-matched `DESTINATIONS` entry when there is one — the only route
 * to the CocoCay and Santorini pages — and otherwise `destination-generic`. There is
 * deliberately no name list for destinations, so one the brief adds later previews on the day
 * it appears instead of dead-ending on `/preview-edit/null/<uid>`.
 *
 * `destination-generic` is the CocoCay layout with a different hero wordmark. Both pages
 * hardcode that asset rather than reading it from the record. ⚠️ The generic one currently
 * carries Paradise Island's, so it names the one destination reaching it today and would
 * misname the second — see `RoyalMomentumDestinationGenericEnhancedNew.md` for the two ways
 * out. It is not an empty shell either: it resolves its content through
 * `findDestination(…, "cococay")`, so a name BE does return matches its own record.
 *
 * @param {object|null} match A `findDestinationEntry` result. Ignored for ships.
 */
export const resolveTemplateType = (match, category, title) => {
    if (isCategory(category, "ship")) {
        return ENHANCED_SHIP_KEYS.has(shipKey(title)) ? "ship" : "ship-standard";
    }
    if (match?.type) return match.type;
    return "destination-generic";
};

/**
 * The slug the store-visit URL carries, e.g. `/store-visit/ship/liberty-of-the-seas/leisure`.
 *
 * Decorative: no page reads it. The ship pages take their subject from `?shipUid=` and the
 * destination pages from a name prop, so `:id` is never consulted. It is derived from the
 * title rather than looked up so that it is never `null` — an unlisted item used to produce
 * `/ship/null/leisure`.
 */
export const resolveSlug = (match, title) => match?.slug ?? slugify(title);
